import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import {
    approveTask,
    ApprovalRecord,
    ApprovalStat,
    ApprovalTask,
    getApproval,
    listApprovals,
    listApprovalStats,
    rejectTask,
} from '../../api/parameter';
import { ApiError } from '../../api/request';
import { useAuthz, useCanAny } from '../../store';
import { allows, approvalResourceOf } from '../../api/authz';
import {
    emitApprovalChanged,
    RESOURCE_LABEL,
    STATUS_LABEL,
} from '../../utils/approvalLabels';
import { formatTime } from '../../utils/time';
import Modal from '../../components/Modal';
import { useDictText } from '../../components/DictSelect';
import { useCapTypes } from '../../components/CapTypeBadges';
import Pagination from '../../components/Pagination';
import SearchSelect from '../../components/SearchSelect';
import { ArchTags } from '../../components/mmode/archs';
import { formatCategoryPair } from '../../utils/categoryOptions';

// 审批中心 §7.6 / §8.1: 普通用户只看自己提交的; admin 默认看全部, 可切换"只看自己".
// 状态: pending / approved / rejected / cancelled.
// 任务两类:
// - 参数行审批: targetType = 工艺框架 code (laser/knife/...), 跳参数管理.
// - 通用变更工单: targetType = 资源类型 (material/dict/...), payload 存变更内容 {action, data}.

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const PAGE_SIZE_STORAGE_KEY = 'approval-list-page-size';

function loadPageSize(): number {
    const n = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return PAGE_SIZE_OPTIONS.includes(n) ? n : PAGE_SIZE_OPTIONS[0];
}

const APPROVAL_STATUS_OPTS = [
    { value: 'pending', label: '待审批' },
    { value: 'approved', label: '已通过' },
    { value: 'rejected', label: '已驳回' },
    { value: 'cancelled', label: '已撤回' },
];
const SCOPE_OPTS = [
    { value: 'all', label: '全部任务' },
    { value: 'mine', label: '仅我提交的' },
];

// payload.action → 中文动作名 (与后端 changereq.actionZh 对齐).
// 工单标题里已含动作词, 但变更内容弹窗要独立标出"这是什么操作", 免得只看字段表猜不出意图.
const ACTION_LABEL: Record<string, string> = {
    create: '新建',
    update: '编辑',
    delete: '删除',
    update_capability: '编辑工艺能力',
    update_phys: '编辑理化参数',
    create_sku: '新建 SKU',
    update_sku: '编辑 SKU',
    delete_sku: '删除 SKU',
    unbind_sku: '解绑 SKU',
    import: '批量导入',
    import_sku: '批量导入 SKU',
    import_xlsx: '批量导入',
    import_yaml: '导入 YAML',
    update_modes: '修改模式能力',
    update_calibration: '编辑功率标定',
    import_calibration: '导入功率标定',
    batch_update: '批量修改',
    patch_fields: '编辑字段',
    update_status: '切换状态',
    mount: '挂载',
    unmount: '解除挂载',
    reorder: '排序',
    apply: '申请',
    apply_roles: '申请权限',
    // 发布 / 取消发布 (2026-09-03): 材料/设备 OpenAPI 对外可见性开关
    publish: '发布',
    unpublish: '取消发布',
    // 批量工单 (2026-09-10): 真正的动作在 payload.data.action 里, 这里只是外壳
    batch: '批量操作',
};

// actionLabelOf 工单的动作中文名. 批量工单的外壳动作恒为 'batch', 真正的动作在
// payload.data.action 里, 这里展开成 "批量删除" / "批量发布", 否则审批列表全是"批量操作".
function actionLabelOf(t: ApprovalTask): string {
    const p = parsePayload(t.payload || '');
    if (!p) return '—';
    if (p.action === 'batch') {
        const inner =
            isPlainObject(p.data) && typeof p.data.action === 'string'
                ? p.data.action
                : '';
        return '批量' + (ACTION_LABEL[inner] || inner || '操作');
    }
    return ACTION_LABEL[p.action] || p.action || '—';
}

// 模块 tab: targetType 传后端做 target_type 过滤 (逗号分隔多值; 特殊值 param
// 由后端展开为 process_arch 注册表全部 code). 空 targetType = 全部.
const MODULE_TABS: { key: string; label: string; targetType?: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'param', label: '参数', targetType: 'param,param_row' },
    { key: 'material', label: '材料', targetType: 'material,material_bundle' },
    { key: 'sku', label: 'SKU', targetType: 'material_sku' },
    {
        key: 'catdict',
        label: '类别·字典',
        targetType:
            'category,dict,dict_category,mm_group,mm_mode,mm_device_ref',
    },
    {
        key: 'device',
        label: '设备',
        targetType:
            'device,device_ext_value,device_material_compat,device_bundle',
    },
    { key: 'gallery', label: '效果图', targetType: 'gallery' },
    { key: 'tool', label: '工具', targetType: 'tool' },
    { key: 'tip', label: '小贴士', targetType: 'tip' },
    // Schema 模板 (显式绑定): 参数/材料/设备三域模板的变更工单
    {
        key: 'schema',
        label: 'Schema 模板',
        targetType:
            'param_schema,phys_schema,device_schema,sku_schema,schema_field',
    },
    { key: 'platform', label: '平台配置', targetType: 'process_arch,cap_type' },
    { key: 'access', label: '访问申请', targetType: 'access_user' },
    { key: 'token', label: '令牌申请', targetType: 'api_token' },
];

// 「我的工单」(mineOnly) 的 tab 收窄 (2026-08-19 管理员管理菜单收回 admin-only 之后):
// 普通用户根本提不了的类型不给 tab —— 提交入口都没有, 列出来永远是空表;
// 混合型 tab 收窄到普通用户确实能提的 target_type. null = 整个 tab 隐藏.
// 注意"全部" tab 不带 targetType, 历史工单 (如以前提过的字典变更) 仍看得到, 不丢记录.
const MINE_TAB_OVERRIDE: Record<
    string,
    { label?: string; targetType?: string } | null
> = {
    // Schema 模板/字段: 维护口只在 Schema 管理页 (管理员管理菜单)
    schema: null,
    // 字典 / 加工模式字典已 admin-only, 只剩材料类别还能提
    catdict: { label: '类别', targetType: 'category' },
    // 设备-材料适配 (device_material_compat) 仅 admin 可发起; 加工模式引用在设备页, 归此
    device: {
        targetType: 'device,device_ext_value,mm_device_ref,device_bundle',
    },
    // 工艺架构维护在字典维护页 (admin), 普通用户只剩工艺类型 (工艺类型在「数据字典」页的工艺类型 tab)
    platform: { label: '工艺类型', targetType: 'cap_type' },
    // 访问申请: 未获权限的申请人进不了我的工单页 (AccessGuard), 已获权限者在"全部"仍可见历史
    access: null,
};

// visibleTabs 按视角取 tab 列表: 审批中心用全量, 我的工单用收窄后的.
function visibleTabs(mineOnly: boolean): typeof MODULE_TABS {
    if (!mineOnly) return MODULE_TABS;
    return MODULE_TABS.flatMap((t) => {
        if (!(t.key in MINE_TAB_OVERRIDE)) return [t];
        const ov = MINE_TAB_OVERRIDE[t.key];
        return ov === null ? [] : [{ ...t, ...ov }];
    });
}

// tab 的待审批徽标计数: stats 是 pending 工单按 target_type 的分组计数.
// "参数" tab 与行渲染同一套判定 — targetType 不在 RESOURCE_LABEL 表内即参数行审批 (工艺框架 code).
function tabPendingCount(
    tab: { key: string; targetType?: string },
    stats: ApprovalStat[],
): number {
    if (tab.key === 'all') {
        return stats.reduce((acc, s) => acc + s.count, 0);
    }
    if (tab.key === 'param') {
        // 参数 tab = 参数行审批 (targetType 为工艺框架 code, 不在资源表内) + 参数行硬删工单 (param_row)
        return stats
            .filter(
                (s) =>
                    !RESOURCE_LABEL[s.targetType] ||
                    s.targetType === 'param_row',
            )
            .reduce((acc, s) => acc + s.count, 0);
    }
    const types = (tab.targetType || '').split(',');
    return stats
        .filter((s) => types.includes(s.targetType))
        .reduce((acc, s) => acc + s.count, 0);
}

// payload (JSON: {action, data}) → pretty 文本; 解析失败时原样展示.
function prettyPayload(payload: string): string {
    try {
        return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
        return payload;
    }
}

// payload 结构 (后端 changereq.Payload): action + data (原请求体) + before (提交时的实体快照).
// before 由后端 2026-08 起写入, 存量旧工单没有 → 回退整段 JSON 展示.
interface ParsedPayload {
    action: string;
    data: unknown;
    before?: unknown;
    // full = data 是目标的全量快照 (参数行审批), 而非只带改动字段的请求体 (变更工单).
    full?: boolean;
}

// 存量参数行工单 payload 是 JSON null (列为 json 类型, 空值落库成 "null"), 视同没有变更内容.
function hasPayload(t: ApprovalTask): boolean {
    return !!t.payload && t.payload !== 'null';
}

function parsePayload(payload: string): ParsedPayload | null {
    try {
        const p = JSON.parse(payload) as ParsedPayload;
        return p && typeof p === 'object' && typeof p.action === 'string'
            ? p
            : null;
    } catch {
        return null;
    }
}

// 单值展示: 空 → '(空)', 布尔 → 启用语义不明确所以用 是/否.
// 空串不能显示成 '—': 提交空串是有意义的变更 (解绑材料 / 清空 CMS 映射),
// 与"该字段未提交"必须能区分开.
function fmtValue(v: unknown): string {
    if (v === null || v === undefined) return '—';
    if (v === '') return '(空)';
    if (typeof v === 'boolean') return v ? '是' : '否';
    return String(v);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isPrimitiveArray(v: unknown): v is (string | number)[] {
    return (
        Array.isArray(v) &&
        v.every((x) => typeof x === 'string' || typeof x === 'number')
    );
}

// 值单元格: 标量直出, 字符串/数字数组切 chips (排序/挂载类动作), 对象与对象数组
// (设备模式集合/标定点集/矩阵…) 折成紧凑 JSON 块, 免得挤成一行读不了.
function ValueCell({ v, tone }: { v: unknown; tone: 'before' | 'after' }) {
    const cls =
        tone === 'before' ? 'text-rose-600' : 'text-emerald-700 font-semibold';
    if (isPrimitiveArray(v)) {
        if (!v.length) return <span className={cls}>—</span>;
        return (
            <div className="flex flex-wrap gap-1">
                {v.map((x, i) => (
                    <span
                        key={`${x}-${i}`}
                        className={`px-1.5 py-0.5 rounded bg-white/70 border border-slate-200 ${cls}`}
                    >
                        {x}
                    </span>
                ))}
            </div>
        );
    }
    if (v !== null && typeof v === 'object') {
        return (
            <pre
                className={`text-[11px] font-mono max-h-40 overflow-auto ${cls}`}
            >
                {JSON.stringify(v, null, 2)}
            </pre>
        );
    }
    if (tone === 'before') {
        return (
            <span className="text-rose-600 line-through decoration-rose-300">
                {fmtValue(v)}
            </span>
        );
    }
    return <span className={cls}>{fmtValue(v)}</span>;
}

type DiffRow = { key: string; before: unknown; after: unknown };

// 字段级 diff. 变更工单的请求体全是指针字段, null = 未修改直接跳过, 只能遍历新值的键;
// 参数行是全量快照 (full), 取并集才能看见"某个参数被删掉了" (新值缺失 → 显示为空).
function diffRows(
    data: Record<string, unknown>,
    before: Record<string, unknown>,
    full = false,
): DiffRow[] {
    const keys = full
        ? Array.from(
              new Set([...Object.keys(before), ...Object.keys(data)]),
          ).sort()
        : Object.keys(data).filter(
              (k) => data[k] !== null && data[k] !== undefined,
          );
    return keys
        .filter(
            (k) =>
                JSON.stringify(data[k] ?? null) !==
                JSON.stringify(before[k] ?? null),
        )
        .map((k) => ({ key: k, before: before[k], after: data[k] }));
}

// 集合类字段 (排序/挂载/键列表) 的差异摘要: 两个长 chips 列表并排看不出改了什么,
// 这里直接点名新增/移除; 集合相同则说明只动了顺序.
function arrayDelta(before: unknown, after: unknown) {
    if (!isPrimitiveArray(before) || !isPrimitiveArray(after)) return null;
    const b = before.map(String);
    const a = after.map(String);
    const added = a.filter((x) => !b.includes(x));
    const removed = b.filter((x) => !a.includes(x));
    if (!added.length && !removed.length) return `仅顺序调整 (${a.length} 项)`;
    const parts = [];
    if (added.length)
        parts.push(`新增 ${added.length} 项: ${added.join(', ')}`);
    if (removed.length)
        parts.push(`移除 ${removed.length} 项: ${removed.join(', ')}`);
    return parts.join(' · ');
}

// diff 配色约定: 旧值红底删除线, 新值绿底加粗.
function DiffTable({ rows }: { rows: DiffRow[] }) {
    if (!rows.length) {
        return (
            <div className="text-xs text-slate-400">
                提交的内容与提交时的实体值无差异
            </div>
        );
    }
    return (
        <table className="w-full text-xs border border-slate-100 rounded overflow-hidden table-fixed">
            <thead>
                <tr className="bg-slate-50 text-slate-500">
                    <th className="text-left font-medium px-3 py-2 w-1/4">
                        字段
                    </th>
                    <th className="text-left font-medium px-3 py-2 text-rose-500">
                        修改前
                    </th>
                    <th className="text-left font-medium px-3 py-2 text-emerald-600">
                        修改后
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => {
                    const delta = arrayDelta(r.before, r.after);
                    return (
                        <React.Fragment key={r.key}>
                            <tr className="border-t border-slate-100 align-top">
                                <td className="px-3 py-2 font-mono text-slate-500 break-all">
                                    {r.key}
                                </td>
                                <td className="px-3 py-2 break-all bg-rose-50/60">
                                    <ValueCell v={r.before} tone="before" />
                                </td>
                                <td className="px-3 py-2 break-all bg-emerald-50/60">
                                    <ValueCell v={r.after} tone="after" />
                                </td>
                            </tr>
                            {delta && (
                                <tr className="bg-slate-50/60">
                                    <td
                                        className="px-3 pb-2 text-[11px] text-slate-500"
                                        colSpan={3}
                                    >
                                        {delta}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
}

// 无对比基准时的整体内容表 (新建/批量导入的提交内容, 删除工单的删除前内容).
// keepEmpty: 编辑类动作要保留空串行 (提交空串 = 有意清空该字段, 见 fmtValue);
// 新建/删除快照场景空串只是噪音, 过滤掉.
function ContentTable({
    data,
    tone,
    keepEmpty,
}: {
    data: Record<string, unknown>;
    tone: 'before' | 'after';
    keepEmpty?: boolean;
}) {
    const rows = Object.entries(data).filter(
        ([, v]) => v !== null && v !== undefined && (keepEmpty || v !== ''),
    );
    if (!rows.length)
        return <div className="text-xs text-slate-400">提交内容为空</div>;
    return (
        <table className="w-full text-xs border border-slate-100 rounded overflow-hidden table-fixed">
            <thead>
                <tr className="bg-slate-50 text-slate-500">
                    <th className="text-left font-medium px-3 py-2 w-1/3">
                        字段
                    </th>
                    <th className="text-left font-medium px-3 py-2">内容</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(([k, v]) => (
                    <tr key={k} className="border-t border-slate-100 align-top">
                        <td className="px-3 py-2 font-mono text-slate-500 break-all">
                            {k}
                        </td>
                        <td
                            className={`px-3 py-2 break-all ${tone === 'before' ? 'bg-rose-50/40' : 'bg-emerald-50/40'}`}
                        >
                            <ValueCell v={v} tone={tone} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// 挂载是追加语义 (已挂载的会被跳过), 直接按覆盖 diff 展示会误导, 单独渲染.
function MountContent({
    data,
    before,
}: {
    data: Record<string, unknown>;
    before?: unknown;
}) {
    const req = isPrimitiveArray(data.xcodes) ? data.xcodes.map(String) : [];
    const cur =
        isPlainObject(before) && isPrimitiveArray(before.xcodes)
            ? before.xcodes.map(String)
            : [];
    const fresh = req.filter((x) => !cur.includes(x));
    const skipped = req.filter((x) => cur.includes(x));
    return (
        <div className="space-y-2 text-xs">
            <div>
                <span className="text-slate-500">
                    本次新增挂载 ({fresh.length}):{' '}
                </span>
                <span className="text-emerald-700 font-semibold break-all">
                    {fresh.length ? fresh.join(', ') : '—'}
                </span>
            </div>
            {skipped.length > 0 && (
                <div className="text-slate-400 break-all">
                    已挂载将被跳过 ({skipped.length}): {skipped.join(', ')}
                </div>
            )}
            <div className="text-slate-400 break-all">
                挂载前已有 ({cur.length}): {cur.length ? cur.join(', ') : '—'}
            </div>
        </div>
    );
}

// 级联删除工单的影响面摘要 (2026-08-21 硬删): 提交人勾选时看到的预检快照 (related)
// 原样进了 payload, 审批人在这里看到同一份 —— 勾了的组标红 (将连带删除), 没勾的给保留说明.
function CascadeSummary({ data }: { data: Record<string, unknown> }) {
    const cascade = isPrimitiveArray(data.cascade)
        ? data.cascade.map(String)
        : [];
    const related = Array.isArray(data.related)
        ? (data.related as {
              key: string;
              label: string;
              count: number;
              required?: boolean;
              uncheckedNote?: string;
              note?: string;
          }[])
        : [];
    if (!related.length) return null;
    return (
        <div className="space-y-1.5 text-xs rounded-lg border border-slate-100 bg-slate-50/60 p-3">
            <div className="font-medium text-slate-600">
                关联数据处理 (提交人勾选):
            </div>
            {related.map((g) => {
                const del = g.required || cascade.includes(g.key);
                return (
                    <div key={g.key} className="flex items-start gap-2">
                        <span
                            className={`tag shrink-0 ${del ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-700'}`}
                        >
                            {del ? '连带删除' : '保留'}
                        </span>
                        <span>
                            {g.label}{' '}
                            <span className="font-mono">× {g.count}</span>
                            {g.required && (
                                <span className="text-slate-400">
                                    {' '}
                                    (从属数据, 必删)
                                </span>
                            )}
                            {g.note && (
                                <span className="block text-slate-400">
                                    {g.note}
                                </span>
                            )}
                            {!del && g.uncheckedNote && (
                                <span className="block text-slate-400">
                                    {g.uncheckedNote}
                                </span>
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

// ───────────────────────── 批量工单 (2026-09-10) ─────────────────────────
// payload.action = 'batch', 真正的目标清单在 data.items 里, 单条动作在 data.action.
// 审批人要判断的是"动了哪些目标 + 关联数据怎么处理", 所以这里给目标清单 + 一份级联摘要
// (批量的 cascade 勾选对整批统一, 取首项的 related 即代表全批口径).

interface BatchPayloadData {
    action: string;
    items: { targetCode: string; data?: Record<string, unknown> }[];
}

// isBatchPayload payload.data 是否为批量结构.
function isBatchPayload(
    data: Record<string, unknown> | null,
): data is Record<string, unknown> & BatchPayloadData {
    return (
        !!data && typeof data.action === 'string' && Array.isArray(data.items)
    );
}

// 目标清单展示上限, 超出折叠 (批量上限 200, 全铺开会把弹窗撑爆).
const BATCH_TARGETS_SHOWN = 60;

function BatchContent({ data }: { data: BatchPayloadData }) {
    const [expanded, setExpanded] = useState(false);
    const items = data.items || [];
    const shown = expanded ? items : items.slice(0, BATCH_TARGETS_SHOWN);
    const first = items.find((it) => isPlainObject(it.data));
    return (
        <div className="space-y-3">
            <div className="text-xs text-slate-500">
                该工单将对 <b className="text-slate-700">{items.length}</b>{' '}
                个目标执行「
                {ACTION_LABEL[data.action] || data.action}」,
                审批通过后在同一事务里逐个执行 —— 任一项失败整批回滚。
            </div>
            {first?.data && <CascadeSummary data={first.data} />}
            {data.action === 'update' && first?.data && (
                <BatchUpdateFields data={first.data} />
            )}
            <div className="flex flex-wrap gap-1">
                {shown.map((it) => (
                    <span
                        key={it.targetCode}
                        className="tag bg-slate-50 text-slate-600 font-mono"
                    >
                        {it.targetCode}
                    </span>
                ))}
                {items.length > BATCH_TARGETS_SHOWN && (
                    <button
                        type="button"
                        className="text-xs text-sky-600 hover:underline"
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded
                            ? '收起'
                            : `展开其余 ${items.length - BATCH_TARGETS_SHOWN} 项`}
                    </button>
                )}
            </div>
        </div>
    );
}

// 批量更新 (/v1/batch/update, 如加工模式字典「迁移」): 各项 data 除键外的字段相同, 取首项展示改成什么.
function BatchUpdateFields({ data }: { data: Record<string, unknown> }) {
    const entries = Object.entries(data).filter(
        ([k, v]) => k !== 'value' && v !== undefined && v !== null,
    );
    if (!entries.length) return null;
    return (
        <div className="text-xs rounded-lg border border-slate-100 bg-slate-50/60 p-3 space-y-1">
            <div className="font-medium text-slate-600">统一改为:</div>
            {entries.map(([k, v]) => (
                <div key={k}>
                    <span className="text-slate-500">{k}</span> ={' '}
                    <span className="font-mono text-slate-700">
                        {String(v)}
                    </span>
                </div>
            ))}
        </div>
    );
}

// ───────────────────────── 套件工单 (device_bundle / material_bundle) ─────────────────────────
// payload.data = {kind, version, bundles:[...]}. 套件是 create-only, 没有"变更前", 所以这里
// 不做左右对比, 而是把整包拆成分段可读视图 + 顶部影响面摘要 —— 审批人真正要判断的是
// "这一包会新增什么、参数行会不会直接生效", 逐段看比读 JSON 快得多.

function asArray(v: unknown): Record<string, unknown>[] {
    return Array.isArray(v) ? v.filter(isPlainObject) : [];
}

function asStrArray(v: unknown): string[] {
    return isPrimitiveArray(v) ? v.map(String) : [];
}

// 段落容器: 标题 + 计数 + 内容.
function Section({
    title,
    count,
    children,
}: {
    title: string;
    count?: number;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1">
            <div className="text-xs font-medium text-slate-600">
                {title}
                {count !== undefined && (
                    <span className="text-slate-400 font-normal">
                        {' '}
                        × {count}
                    </span>
                )}
            </div>
            {children}
        </div>
    );
}

// 数组段的紧凑表 (模式/SKU/参数行等): cols = [表头, 取值函数].
function MiniTable({
    cols,
    rows,
    max = 10,
}: {
    cols: [string, (r: Record<string, unknown>) => React.ReactNode][];
    rows: Record<string, unknown>[];
    max?: number;
}) {
    const [all, setAll] = useState(false);
    if (!rows.length) return <div className="text-xs text-slate-400">—</div>;
    const shown = all ? rows : rows.slice(0, max);
    return (
        <div className="space-y-1">
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-slate-100 rounded">
                    <thead>
                        <tr className="bg-slate-50 text-slate-500">
                            {cols.map(([h]) => (
                                <th
                                    key={h}
                                    className="text-left font-medium px-2 py-1.5 whitespace-nowrap"
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((r, i) => (
                            <tr
                                key={i}
                                className="border-t border-slate-100 align-top"
                            >
                                {cols.map(([h, get]) => (
                                    <td
                                        key={h}
                                        className="px-2 py-1.5 break-all"
                                    >
                                        {get(r)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {rows.length > max && (
                <button
                    type="button"
                    className="text-xs text-brand-600 hover:underline"
                    onClick={() => setAll(!all)}
                >
                    {all ? '收起' : `展开全部 ${rows.length} 行`}
                </button>
            )}
        </div>
    );
}

// 字段组值: flat 组直接 key:value; matrix 组是 {行: {字段: 值}}, 按行分组展示.
function GroupValues({ groups }: { groups: Record<string, unknown> }) {
    const entries = Object.entries(groups);
    if (!entries.length) return <div className="text-xs text-slate-400">—</div>;
    return (
        <div className="space-y-2">
            {entries.map(([group, vals]) => {
                const obj = isPlainObject(vals) ? vals : {};
                const nested =
                    Object.values(obj).every(isPlainObject) &&
                    Object.keys(obj).length > 0;
                return (
                    <div
                        key={group}
                        className="rounded border border-slate-100"
                    >
                        <div className="px-2 py-1 bg-slate-50 text-xs font-mono text-slate-600">
                            {group}
                        </div>
                        <div className="p-2 space-y-1">
                            {nested ? (
                                Object.entries(obj).map(([row, fields]) => (
                                    <div key={row} className="text-xs">
                                        <span className="font-mono text-slate-500">
                                            {row}
                                        </span>
                                        <span className="text-slate-400">
                                            {' '}
                                            ·{' '}
                                        </span>
                                        <span className="break-all">
                                            {kvInline(fields)}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="text-xs break-all">
                                    {kvInline(obj)}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// 对象压成 "k=v · k=v" 一行 (参数值/字段组值这类小对象, 比嵌套表格好读).
function kvInline(v: unknown): string {
    if (!isPlainObject(v)) return fmtValue(v);
    const parts = Object.entries(v).map(([k, val]) => `${k}=${fmtValue(val)}`);
    return parts.length ? parts.join(' · ') : '—';
}

function chips(vals: string[]): React.ReactNode {
    if (!vals.length) return <span className="text-xs text-slate-400">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {vals.map((v) => (
                <span
                    key={v}
                    className="tag bg-slate-100 text-slate-600 font-mono"
                >
                    {v}
                </span>
            ))}
        </div>
    );
}

// 单个套件的分段视图 (设备 / 材料两种 kind).
function BundleCard({
    kind,
    b,
    index,
    total,
}: {
    kind: string;
    b: Record<string, unknown>;
    index: number;
    total: number;
}) {
    const isDevice = kind === 'device-bundle';
    const head = isPlainObject(b.device)
        ? b.device
        : isPlainObject(b.material)
          ? b.material
          : {};
    // 词表一律翻中文再给审批人看: 工单里躺着 craft/surface/color 的 code, 审批人不背字典.
    const craftText = useDictText('craft');
    const surfaceText = useDictText('surface');
    const colorText = useDictText('color');
    const { capTypes } = useCapTypes();
    const capLabel = (k: string) =>
        capTypes.find((c) => c.capKey === k)?.label || k;
    const title = isDevice
        ? String(head.deviceKey || '(缺 deviceKey)')
        : String(head.xcode || '') || `自动取号 (${String(head.type2 || '')})`;
    const params = asArray(b.params);
    const skus = asArray(b.skus);
    const modeRefs = asArray(b.modeRefs);
    const modeSwitches = asArray(b.modeSwitches);
    const calibration = asArray(b.calibration);
    const caps = asStrArray(b.capabilities);
    const extValues = isPlainObject(b.extValues) ? b.extValues : {};
    const physValues = isPlainObject(b.physValues) ? b.physValues : {};
    const tips = isPlainObject(b.tips) ? b.tips : null;
    const bindings = isPlainObject(b.schemaBindings) ? b.schemaBindings : null;
    // 随套件新建的字典 (2026-09-29): 模式组/模式值是全平台共用字典, 审批人要看得见;
    // 字段组: 设备套件建在本框架模板下 (deviceSchemas), 材料套件建在本一级类别模板下 (physSchemas)
    const newDicts = isPlainObject(b.newDicts) ? b.newDicts : {};
    const newGroups = asArray(newDicts.modeGroups);
    const newModes = asArray(newDicts.modes);
    const newSchemas = asArray(
        isDevice ? newDicts.deviceSchemas : newDicts.physSchemas,
    );
    const newDictTotal = newGroups.length + newModes.length + newSchemas.length;

    // 影响面摘要: 审批人最先要看的一行.
    const wantsPublish = head.publish === true;
    const impact = [
        isDevice ? `模式引用 ${modeRefs.length}` : `SKU ${skus.length}`,
        isDevice ? `模式开关 ${modeSwitches.length}` : `能力位 ${caps.length}`,
        isDevice
            ? `字段组 ${Object.keys(extValues).length}`
            : `理化组 ${Object.keys(physValues).length}`,
        isDevice
            ? `校准点 ${calibration.length}`
            : `贴士 ${asStrArray(tips?.tipCodes).length}`,
        `参数行 ${params.length}`,
        ...(newDictTotal ? [`字典项 ${newDictTotal}`] : []),
    ];

    return (
        // 默认全展开 (2026-09-16): 审批人要判断的是"这一包到底建了什么",
        // 折叠起来等于让他一个个点开 —— 套件工单本来就是一次审完整包.
        <details open className="rounded-lg border border-slate-200">
            <summary className="cursor-pointer select-none px-3 py-2 bg-slate-50 rounded-t-lg">
                <span className="text-sm font-semibold">{title}</span>
                {total > 1 && (
                    <span className="text-xs text-slate-400">
                        {' '}
                        · bundles[{index}]
                    </span>
                )}
                {wantsPublish && (
                    <span className="tag bg-amber-50 text-amber-700 ml-2">
                        申请创建后立即发布
                    </span>
                )}
                <div className="text-[11px] text-slate-500 mt-0.5">
                    新增 {impact.join(' · ')}
                </div>
            </summary>
            <div className="p-3 space-y-3">
                <Section title={isDevice ? '设备主数据' : '材料主数据'}>
                    <ContentTable
                        data={
                            isDevice
                                ? head
                                : {
                                      ...head,
                                      craft1: craftText(
                                          String(head.craft1 || ''),
                                      ),
                                      craft2: craftText(
                                          String(head.craft2 || ''),
                                      ),
                                      surface1: surfaceText(
                                          String(head.surface1 || ''),
                                      ),
                                      surface2: surfaceText(
                                          String(head.surface2 || ''),
                                      ),
                                      color: colorText(
                                          String(head.color || ''),
                                      ),
                                  }
                        }
                        tone="after"
                    />
                </Section>

                {newDictTotal > 0 && (
                    <Section
                        title={
                            isDevice
                                ? '随套件新建的字典 (先于设备创建)'
                                : '随套件新建的理化字段组'
                        }
                        count={newDictTotal}
                    >
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            {isDevice ? (
                                <>
                                    ⚠ 模式组 / 模式值是<b>全平台共用字典</b>,
                                    通过本工单即创建, 不再单独走字典审批;
                                    字段组建在框架 {fmtValue(head.archCode)} 下
                                </>
                            ) : (
                                <>
                                    ⚠ 字段组建在一级类别 {fmtValue(head.type1)}{' '}
                                    的模板下 (同类别其他材料也会看到),
                                    通过本工单即创建, 不再单独走模板审批
                                </>
                            )}
                        </div>
                        {newGroups.length > 0 && (
                            <MiniTable
                                rows={newGroups}
                                cols={[
                                    [
                                        '模式组',
                                        (r) => (
                                            <span className="font-mono">
                                                {fmtValue(r.code)}
                                            </span>
                                        ),
                                    ],
                                    ['中文名', (r) => fmtValue(r.nameZh)],
                                    [
                                        '适用框架',
                                        // 10-22 前提交的老工单载荷里是 headType, 没有 archCodes 时原样显示
                                        (r) =>
                                            Array.isArray(r.archCodes) || !r.headType ? (
                                                <ArchTags codes={r.archCodes as string[] | undefined} />
                                            ) : (
                                                fmtValue(r.headType)
                                            ),
                                    ],
                                ]}
                            />
                        )}
                        {newModes.length > 0 && (
                            <MiniTable
                                rows={newModes}
                                cols={[
                                    [
                                        '模式值',
                                        (r) => (
                                            <span className="font-mono">
                                                {fmtValue(r.value)}
                                            </span>
                                        ),
                                    ],
                                    ['中文名', (r) => fmtValue(r.nameZh)],
                                    [
                                        '归属组',
                                        (r) => (
                                            <span className="font-mono">
                                                {fmtValue(r.groupCode)}
                                            </span>
                                        ),
                                    ],
                                ]}
                            />
                        )}
                        {newSchemas.length > 0 && (
                            <MiniTable
                                rows={newSchemas}
                                cols={[
                                    [
                                        '字段组',
                                        (r) => (
                                            <span className="font-mono">
                                                {fmtValue(r.groupKey)}
                                            </span>
                                        ),
                                    ],
                                    ['名称', (r) => fmtValue(r.label)],
                                    [
                                        '字段',
                                        (r) => {
                                            try {
                                                const fs = JSON.parse(
                                                    String(r.fields || '[]'),
                                                ) as { key?: string }[];
                                                return fs
                                                    .map((f) => f.key)
                                                    .join(', ');
                                            } catch {
                                                return fmtValue(r.fields);
                                            }
                                        },
                                    ],
                                    [
                                        isDevice ? '适用设备' : '适用二级类别',
                                        (r) =>
                                            asStrArray(
                                                isDevice
                                                    ? r.deviceKeys
                                                    : r.cat2Keys,
                                            ).join(', ') ||
                                            (isDevice
                                                ? '框架全部设备'
                                                : '类别全部材料'),
                                    ],
                                ]}
                            />
                        )}
                    </Section>
                )}

                {isDevice && modeRefs.length > 0 && (
                    <Section title="加工模式引用" count={modeRefs.length}>
                        <MiniTable
                            rows={modeRefs}
                            cols={[
                                [
                                    '模式值',
                                    (r) => (
                                        <span className="font-mono">
                                            {fmtValue(r.modeValue)}
                                        </span>
                                    ),
                                ],
                                [
                                    '别名',
                                    (r) => fmtValue(r.aliasZh ?? r.aliasEn),
                                ],
                                [
                                    '速度范围',
                                    (r) =>
                                        `${fmtValue(r.speedMin)} ~ ${fmtValue(r.speedMax)}`,
                                ],
                            ]}
                        />
                    </Section>
                )}

                {isDevice && modeSwitches.length > 0 && (
                    <Section title="模式能力开关" count={modeSwitches.length}>
                        <MiniTable
                            rows={modeSwitches}
                            cols={[
                                [
                                    '模式',
                                    (r) => (
                                        <span className="font-mono">
                                            {fmtValue(r.mode)}
                                        </span>
                                    ),
                                ],
                                ['启用', (r) => fmtValue(r.isEnabled)],
                                [
                                    '速度范围',
                                    (r) =>
                                        `${fmtValue(r.speedMin)} ~ ${fmtValue(r.speedMax)}`,
                                ],
                            ]}
                        />
                        <div className="text-[11px] text-slate-400 mt-1">
                            速度会同步写入 mode_capability 矩阵 (单源写两处)
                        </div>
                    </Section>
                )}

                {!isDevice && skus.length > 0 && (
                    <Section title="SKU" count={skus.length}>
                        <MiniTable
                            rows={skus}
                            cols={[
                                [
                                    'SKU 编码',
                                    (r) => (
                                        <span className="font-mono">
                                            {fmtValue(r.skuCode)}
                                        </span>
                                    ),
                                ],
                                [
                                    '商品名',
                                    (r) =>
                                        fmtValue(
                                            r.productNameZh ?? r.productNameEn,
                                        ),
                                ],
                                ['属性', (r) => fmtValue(r.prop)],
                                ['CMS ID', (r) => fmtValue(r.cmsId)],
                            ]}
                        />
                    </Section>
                )}

                {!isDevice && caps.length > 0 && (
                    <Section title="工艺能力位" count={caps.length}>
                        {chips(caps.map(capLabel))}
                    </Section>
                )}

                {isDevice && Object.keys(extValues).length > 0 && (
                    <Section
                        title="设备字段组值"
                        count={Object.keys(extValues).length}
                    >
                        <GroupValues groups={extValues} />
                    </Section>
                )}

                {!isDevice && Object.keys(physValues).length > 0 && (
                    <Section
                        title="理化参数"
                        count={Object.keys(physValues).length}
                    >
                        <GroupValues groups={physValues} />
                    </Section>
                )}

                {isDevice && calibration.length > 0 && (
                    <Section title="功率标定曲线" count={calibration.length}>
                        <div className="text-xs break-all">
                            {calibration
                                .map(
                                    (p) =>
                                        `${fmtValue(p.pct)}% → ${fmtValue(p.realW)}W`,
                                )
                                .join('  ·  ')}
                        </div>
                    </Section>
                )}

                {!isDevice && tips && (
                    <Section title="小贴士挂载">
                        {chips(asStrArray(tips.tipCodes))}
                        {tips.includeRecommended === true && (
                            <div className="text-[11px] text-slate-400 mt-1">
                                并入该一级类别的通用推荐条目 (清单以落库时为准)
                            </div>
                        )}
                    </Section>
                )}

                {bindings && (
                    <Section title="模板 (字段组追加本设备 / 参数模板复制给本设备)">
                        <div className="text-xs space-y-1">
                            <div>
                                字段组:{' '}
                                {chips(asStrArray(bindings.deviceSchemas))}
                            </div>
                            <div className="text-slate-500">
                                复制参数模板:{' '}
                                {chips(
                                    asArray(bindings.paramSchemas).map((k) => {
                                        const t = isPlainObject(k) ? k : {};
                                        return [
                                            t.deviceKey || (t.family ? `机型 ${String(t.family)}` : '任意设备'),
                                            t.groupCode || '任意模式组',
                                            t.processType || '任意加工类型',
                                        ].map(String).join(' / ');
                                    }),
                                )}
                            </div>
                        </div>
                    </Section>
                )}

                {params.length > 0 && (
                    <Section title="加工参数行" count={params.length}>
                        <MiniTable
                            rows={params}
                            cols={[
                                [
                                    isDevice ? '材料' : '框架 / 设备',
                                    (r) => (
                                        <span className="font-mono">
                                            {isDevice
                                                ? fmtValue(
                                                      r.xcode ??
                                                          r.filmXcode ??
                                                          (formatCategoryPair(
                                                              r.type1,
                                                              r.type2,
                                                          ) ||
                                                              undefined),
                                                  )
                                                : `${fmtValue(r.archCode)} / ${fmtValue(r.deviceKey)}`}
                                        </span>
                                    ),
                                ],
                                ['子工艺', (r) => fmtValue(r.subProcess)],
                                [
                                    '参数',
                                    (r) => (
                                        <span className="text-slate-600">
                                            {kvInline(r.params)}
                                        </span>
                                    ),
                                ],
                                ['检查结果', (r) => fmtValue(r.resultTag)],
                            ]}
                        />
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                            ⚠ 这 {params.length} 条参数随本工单一并审批: 通过后
                            <b>直接发布 (published)</b>, 不再单独走参数审批
                        </div>
                    </Section>
                )}
            </div>
        </details>
    );
}

// 套件工单内容: 顶部总览 + 逐套件分段.
function BundleContent({ data }: { data: Record<string, unknown> }) {
    const kind = String(data.kind || '');
    const bundles = asArray(data.bundles);
    if (!bundles.length)
        return <div className="text-xs text-slate-400">套件内容为空</div>;
    const totalParams = bundles.reduce(
        (n, b) => n + asArray(b.params).length,
        0,
    );
    return (
        <div className="space-y-2">
            <div className="text-xs text-slate-600 bg-slate-50 rounded px-3 py-2">
                {kind === 'device-bundle' ? '设备套件' : '材料套件'} · 共{' '}
                {bundles.length} 条套件 · 参数行合计 {totalParams} 条 ·
                审批通过后<b>单事务</b>落库 (任一步失败整体回滚)
            </div>
            {bundles.map((b, i) => (
                <BundleCard
                    key={i}
                    kind={kind}
                    b={b}
                    index={i}
                    total={bundles.length}
                />
            ))}
        </div>
    );
}

// 参数行批量工单 (2026-09-30, param_row 域): 令牌 (material-data skill) 经 changes/submit 提的批量导入 / 批量修改.
// 一张单最多 2000 行, 通用字段表会把 rows 糊成一整坨 JSON, 这里按行出表.
// 两种都是"通过即发布 (published)", 不再逐行走参数评审 —— 审批人要知道自己批的是生效数据.

// 参数行的材料键 (按框架粒度只会有其中一种).
function rowMaterialKey(r: Record<string, unknown>): string {
    const film = r.filmXcode ? String(r.filmXcode) : '';
    const sub = r.substrateXcode ? ` + ${String(r.substrateXcode)}` : '';
    return String(
        r.xcode ||
            (film && film + sub) ||
            formatCategoryPair(r.type1, r.type2) ||
            '—',
    );
}

function PublishNotice({ n }: { n: number }) {
    return (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            ⚠ 这 {n} 行参数随本工单一并审批: 通过后<b>直接发布 (published)</b>,
            对外立即可见; 单事务落库, 任一行校验失败整单不生效
        </div>
    );
}

// param_row/import: {archCode, tplId, preset, rows[]}. 预设是整批固定值, 行里没填的按预设落库.
function ParamImportContent({ data }: { data: Record<string, unknown> }) {
    const rows = asArray(data.rows);
    const preset = isPlainObject(data.preset) ? data.preset : {};
    const presetText = [
        preset.deviceKey && `设备 ${String(preset.deviceKey)}`,
        preset.processingMode && `加工模式 ${String(preset.processingMode)}`,
        preset.subProcess && `加工类型 ${String(preset.subProcess)}`,
    ].filter(Boolean);
    return (
        <div className="space-y-2">
            <div className="text-xs text-slate-600 bg-slate-50 rounded px-3 py-2">
                框架{' '}
                <span className="font-mono">{fmtValue(data.archCode)}</span> ·
                参数模板 <b>#{fmtValue(data.tplId)}</b> · 共 {rows.length} 行
                {presetText.length > 0 && (
                    <> · 预设: {presetText.join(' / ')}</>
                )}
            </div>
            <MiniTable
                max={50}
                rows={rows}
                cols={[
                    ['#', (r) => rows.indexOf(r) + 1],
                    [
                        '材料',
                        (r) => (
                            <span className="font-mono">
                                {rowMaterialKey(r)}
                            </span>
                        ),
                    ],
                    [
                        '设备',
                        (r) => (
                            <span className="font-mono">
                                {fmtValue(r.deviceKey || preset.deviceKey)}
                            </span>
                        ),
                    ],
                    [
                        '加工类型',
                        (r) => fmtValue(r.subProcess || preset.subProcess),
                    ],
                    [
                        '参数',
                        (r) => (
                            <span className="text-slate-600">
                                {kvInline(r.params)}
                            </span>
                        ),
                    ],
                    ['检查结果', (r) => fmtValue(r.resultTag)],
                    ['备注', (r) => fmtValue(r.remark)],
                ]}
            />
            <PublishNotice n={rows.length} />
        </div>
    );
}

// param_row/batch_update: data = {archCode, rows:[{id, params?, resultTag?, subProcess?, remark?}]},
// before = 同形快照 (只含被改的字段 + 行的材料/设备键). 摊平成 "行 × 字段" 一格一行的前后对比.
function ParamBatchUpdateContent({
    data,
    before,
}: {
    data: Record<string, unknown>;
    before: Record<string, unknown> | null;
}) {
    const rows = asArray(data.rows);
    const beforeByID = new Map<string, Record<string, unknown>>();
    for (const b of asArray(before?.rows)) beforeByID.set(String(b.id), b);
    const cells: Record<string, unknown>[] = [];
    for (const r of rows) {
        const b = beforeByID.get(String(r.id)) || {};
        const bParams = isPlainObject(b.params) ? b.params : {};
        const base = { id: r.id, mat: rowMaterialKey(b), dev: b.deviceKey };
        if (isPlainObject(r.params)) {
            for (const [k, v] of Object.entries(r.params)) {
                cells.push({
                    ...base,
                    field: k,
                    before: bParams[k],
                    after: v,
                    cleared: v === null,
                });
            }
        }
        for (const k of ['resultTag', 'subProcess', 'remark']) {
            if (k in r)
                cells.push({ ...base, field: k, before: b[k], after: r[k] });
        }
    }
    return (
        <div className="space-y-2">
            <div className="text-xs text-slate-600 bg-slate-50 rounded px-3 py-2">
                框架{' '}
                <span className="font-mono">{fmtValue(data.archCode)}</span> ·
                修改 {rows.length} 行参数, 共 {cells.length} 处字段
                {!before && ' · 建单时未留存修改前快照, 只能看提交值'}
            </div>
            <MiniTable
                max={50}
                rows={cells}
                cols={[
                    [
                        '行 id',
                        (c) => (
                            <span className="font-mono">{fmtValue(c.id)}</span>
                        ),
                    ],
                    [
                        '材料',
                        (c) => (
                            <span className="font-mono">{fmtValue(c.mat)}</span>
                        ),
                    ],
                    [
                        '设备',
                        (c) => (
                            <span className="font-mono">{fmtValue(c.dev)}</span>
                        ),
                    ],
                    [
                        '字段',
                        (c) => (
                            <span className="font-mono">{String(c.field)}</span>
                        ),
                    ],
                    ['修改前', (c) => <ValueCell v={c.before} tone="before" />],
                    [
                        '修改后',
                        (c) =>
                            c.cleared ? (
                                <span className="text-slate-500">
                                    (清除该字段)
                                </span>
                            ) : (
                                <ValueCell v={c.after} tone="after" />
                            ),
                    ],
                ]}
            />
            <PublishNotice n={rows.length} />
        </div>
    );
}

// isParamImportPayload param_row/import 载荷 (材料批量导入也叫 import, 靠 tplId + rows 区分).
function isParamImportPayload(data: Record<string, unknown> | null): boolean {
    return !!data && 'tplId' in data && Array.isArray(data.rows);
}

// 参数模板工单 (param_schema): update = 整份覆盖字段清单 (data.fields 是 JSON 字符串), patch_fields = 按字段修改
// (data.set / data.remove, 审批通过时合并到当时最新的模板). before 是提交时的模板快照 (fields 已展开成数组).
// 通用字段表只会把 fields 糊成一整段 JSON, 这里逐字段对比: 新增 / 删除 / 改了哪个属性.

type SchemaSpec = Record<string, unknown>;

function parseSpecs(v: unknown): SchemaSpec[] {
    if (Array.isArray(v)) return v.filter(isPlainObject);
    if (typeof v === 'string') {
        try {
            const arr = JSON.parse(v);
            return Array.isArray(arr) ? arr.filter(isPlainObject) : [];
        } catch {
            return [];
        }
    }
    return [];
}

// applySchemaPatch 在提交时的快照上预演补丁 (与后端 mergeSchemaPatch 同规则: 按 k 合并, null 去掉属性, 新增追加末尾).
function applySchemaPatch(
    base: SchemaSpec[],
    data: Record<string, unknown>,
): SchemaSpec[] {
    const out = base.map((f) => ({ ...f }));
    for (const s of asArray(data.set)) {
        const k = String(s.k || '');
        if (!k) continue;
        let spec = out.find((f) => f.k === k);
        if (!spec) {
            spec = { k };
            out.push(spec);
        }
        for (const [a, v] of Object.entries(s)) {
            if (v === null) delete spec[a];
            else spec[a] = v;
        }
    }
    const removed = new Set(asStrArray(data.remove));
    return out.filter((f) => !removed.has(String(f.k)));
}

type SchemaDiffRow = {
    k: string;
    kind: '新增' | '删除' | '修改';
    attr: string;
    before: unknown;
    after: unknown;
};

function diffSchemaSpecs(
    before: SchemaSpec[],
    after: SchemaSpec[],
): SchemaDiffRow[] {
    const rows: SchemaDiffRow[] = [];
    const bMap = new Map(before.map((f) => [String(f.k), f]));
    const aMap = new Map(after.map((f) => [String(f.k), f]));
    for (const f of after) {
        const k = String(f.k);
        const b = bMap.get(k);
        if (!b) {
            rows.push({
                k,
                kind: '新增',
                attr: '',
                before: undefined,
                after: f,
            });
            continue;
        }
        const attrs = new Set([...Object.keys(b), ...Object.keys(f)]);
        attrs.delete('k');
        for (const a of attrs) {
            if (JSON.stringify(b[a] ?? null) !== JSON.stringify(f[a] ?? null)) {
                rows.push({
                    k,
                    kind: '修改',
                    attr: a,
                    before: b[a],
                    after: f[a],
                });
            }
        }
    }
    for (const b of before) {
        const k = String(b.k);
        if (!aMap.has(k))
            rows.push({
                k,
                kind: '删除',
                attr: '',
                before: b,
                after: undefined,
            });
    }
    return rows;
}

// 字段规格属性的中文名 (键是模板 JSON 的落库口径).
const SPEC_ATTR_LABEL: Record<string, string> = {
    t: '类型',
    u: '单位',
    v: '默认值',
    o: '内联枚举',
    os: '枚举来源',
    min: '最小值',
    max: '最大值',
    required: '必填',
    label: '显示名快照',
};

function ParamSchemaContent({
    action,
    data,
    before,
}: {
    action: string;
    data: Record<string, unknown>;
    before: Record<string, unknown> | null;
}) {
    const beforeSpecs = parseSpecs(before?.fields);
    const afterSpecs =
        action === 'patch_fields'
            ? applySchemaPatch(beforeSpecs, data)
            : parseSpecs(data.fields);
    const rows = diffSchemaSpecs(beforeSpecs, afterSpecs);
    const seg = (v: unknown) => (v ? String(v) : '任意');
    const devBefore = asStrArray(before?.deviceKeys);
    const devAfter =
        'deviceKeys' in data ? asStrArray(data.deviceKeys) : devBefore;
    const devChanged = devBefore.join(',') !== devAfter.join(',');
    const kindCls = {
        新增: 'text-emerald-700',
        删除: 'text-rose-600',
        修改: 'text-amber-700',
    };
    return (
        <div className="space-y-2">
            <div className="text-xs text-slate-600 bg-slate-50 rounded px-3 py-2">
                模板{' '}
                <span className="font-mono">{fmtValue(data.archCode)}</span> ·
                {data.deviceKey
                    ? `设备 ${String(data.deviceKey)}`
                    : `机型 ${seg(data.family)}`}{' '}
                · 模式组 {seg(data.groupCode)} ·
                加工类型 {seg(data.processType)}
                {' · '}
                {before
                    ? `改前 ${beforeSpecs.length} 个字段 → 改后 ${afterSpecs.length} 个`
                    : '建单时无快照 (新建模板或旧工单), 以下按提交内容展示'}
            </div>
            {rows.length === 0 && !devChanged ? (
                <div className="text-xs text-slate-400">字段没有变化</div>
            ) : (
                <MiniTable
                    max={50}
                    rows={rows as unknown as Record<string, unknown>[]}
                    cols={[
                        [
                            '字段',
                            (r) => (
                                <span className="font-mono">{String(r.k)}</span>
                            ),
                        ],
                        [
                            '变更',
                            (r) => (
                                <span
                                    className={
                                        kindCls[r.kind as SchemaDiffRow['kind']]
                                    }
                                >
                                    {String(r.kind)}
                                </span>
                            ),
                        ],
                        [
                            '属性',
                            (r) =>
                                r.attr
                                    ? `${SPEC_ATTR_LABEL[String(r.attr)] || ''} ${String(r.attr)}`
                                    : '整个字段',
                        ],
                        [
                            '修改前',
                            (r) =>
                                r.before === undefined ? (
                                    '—'
                                ) : (
                                    <ValueCell v={r.before} tone="before" />
                                ),
                        ],
                        [
                            '修改后',
                            (r) =>
                                r.after === undefined ? (
                                    '(去掉)'
                                ) : (
                                    <ValueCell v={r.after} tone="after" />
                                ),
                        ],
                    ]}
                />
            )}
            {devChanged && (
                <div className="text-xs">
                    设备绑定: <ValueCell v={devBefore} tone="before" /> →{' '}
                    <ValueCell v={devAfter} tone="after" />
                </div>
            )}
            {action === 'patch_fields' && (
                <div className="text-[11px] text-slate-500">
                    按字段修改: 审批通过时合并到当时最新的模板上,
                    提单后别人对其它字段的改动不会被覆盖;
                    上表按提交时的快照预演.
                </div>
            )}
        </div>
    );
}

// isParamSchemaPayload param_schema 的 update / patch_fields 载荷 (模板键四段 + fields 或 set/remove).
function isParamSchemaPayload(
    action: string,
    data: Record<string, unknown> | null,
): boolean {
    if (
        !data ||
        !('archCode' in data) ||
        !('processType' in data || 'groupCode' in data || 'family' in data)
    ) {
        return false;
    }
    if (action === 'patch_fields') return true;
    return action === 'update' && typeof data.fields === 'string';
}

// isBundlePayload data 是套件包裹结构 (kind + bundles).
function isBundlePayload(data: Record<string, unknown> | null): boolean {
    if (!data) return false;
    const kind = data.kind;
    return (
        (kind === 'device-bundle' || kind === 'material-bundle') &&
        Array.isArray(data.bundles)
    );
}

// 动作分类: 删除类看"删除前内容", 新建/导入类看"提交内容", 其余走字段级前后对比.
const DELETE_ACTIONS = ['delete', 'delete_sku', 'unmount'];
const CREATE_ACTIONS = ['create', 'create_sku'];

// 访问申请工单内容: 申请人 + 理由 + 勾选的角色 (角色显示名随 my-roles 一起下发, 免调管理接口).
function AccessApplyContent({
    data,
    roleLabels,
}: {
    data: Record<string, unknown>;
    roleLabels: Record<string, string>;
}) {
    const roles = Array.isArray(data.roles) ? (data.roles as string[]) : [];
    const rows: [string, React.ReactNode][] = [
        [
            '申请人',
            `${String(data.displayName || '')} <${String(data.email || '')}>`,
        ],
        [
            '申请理由',
            String(data.reason || '') || (
                <span className="text-slate-400">—</span>
            ),
        ],
        [
            '申请角色',
            roles.length === 0 ? (
                <span className="text-slate-400">仅普通用户</span>
            ) : (
                <span className="flex flex-wrap gap-1">
                    {roles.map((r) => (
                        <span
                            key={r}
                            className="tag bg-brand-50 text-brand-700"
                            title={r}
                        >
                            {roleLabels[r] || r}
                        </span>
                    ))}
                </span>
            ),
        ],
    ];
    return (
        <table className="w-full text-sm">
            <tbody>
                {rows.map(([k, v]) => (
                    <tr key={k} className="border-t border-slate-100">
                        <td className="py-1.5 pr-4 text-slate-500 whitespace-nowrap w-24">
                            {k}
                        </td>
                        <td className="py-1.5">{v}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// 变更内容渲染: 有 before 快照走前后对比表, 否则按动作语义整体展示; 原始 JSON 始终可展开.
function PayloadContent({ payload }: { payload: string }) {
    const { roleLabels } = useAuthz();
    const parsed = parsePayload(payload);
    const rawJson = (
        <details className="text-xs">
            <summary className="cursor-pointer text-slate-400 select-none">
                查看原始 JSON
            </summary>
            <pre className="mt-2 bg-slate-50 border border-slate-100 rounded p-3 font-mono overflow-x-auto max-h-[40vh]">
                {prettyPayload(payload)}
            </pre>
        </details>
    );
    const wrap = (node: React.ReactNode, hint?: string) => (
        <div className="space-y-3">
            {hint && <div className="text-xs text-slate-500">{hint}</div>}
            {node}
            {rawJson}
        </div>
    );

    if (!parsed) return wrap(null);
    const action = parsed.action;
    const data = isPlainObject(parsed.data) ? parsed.data : null;
    const before = isPlainObject(parsed.before) ? parsed.before : null;

    // 访问申请 / 追加申请权限 (access_user): 审批人要看到申请的是哪些角色, 通用字段表只会糊出 roles 数组.
    if ((action === 'apply' || action === 'apply_roles') && data) {
        return wrap(
            <AccessApplyContent data={data} roleLabels={roleLabels} />,
            action === 'apply_roles'
                ? '该用户已准入, 申请追加以下资源管理员角色 (通过后追加绑定, 驳回不影响现有权限):'
                : '该用户申请进入平台, 通过后授予普通用户 + 以下勾选的资源管理员角色:',
        );
    }

    // 批量工单 (2026-09-10): 目标清单 + 统一的级联口径; 批量单不留 before 快照
    // (逐项取等于循环内查询), 逐字段 diff 的需求走单条提交.
    if (action === 'batch' && isBatchPayload(data)) {
        return wrap(<BatchContent data={data} />);
    }

    // 删除类: 展示删除前的完整内容 (没有快照的存量旧单只能回退 JSON);
    // 硬删 + 级联勾选 (2026-08-21): data 里带预检快照 related + 勾选 cascade, 渲染影响面.
    if (DELETE_ACTIONS.includes(action)) {
        const cascadeNode = data ? <CascadeSummary data={data} /> : null;
        return before
            ? wrap(
                  <>
                      {cascadeNode}
                      <ContentTable data={before} tone="before" />
                  </>,
                  '该工单将删除以下内容:',
              )
            : wrap(
                  cascadeNode,
                  '该工单为删除操作' +
                      (cascadeNode ? ':' : ' (建单时未留存快照, 见原始 JSON)'),
              );
    }

    // 挂载: 追加语义, 单独算真正新增的部分
    if (action === 'mount' && data) {
        return wrap(<MountContent data={data} before={parsed.before} />);
    }

    // 套件工单 (device_bundle / material_bundle): 整包按段拆开, 不走通用字段表
    // (data 只有 kind/version/bundles 三个顶层键, 通用表会把整个 bundles 数组糊成一坨 JSON).
    if (isBundlePayload(data) && data) {
        return wrap(<BundleContent data={data} />, '该工单将新增以下套件数据:');
    }

    // 参数模板整份覆盖 / 按字段修改: 逐字段对比
    if (data && isParamSchemaPayload(action, data)) {
        return wrap(
            <ParamSchemaContent action={action} data={data} before={before} />,
            '该工单将修改以下参数模板字段:',
        );
    }

    // 参数行批量导入 / 批量修改 (令牌提的整批工单): 按行出表
    if (action === 'import' && data && isParamImportPayload(data)) {
        return wrap(
            <ParamImportContent data={data} />,
            '该工单将新增以下参数行:',
        );
    }
    if (action === 'batch_update' && data && Array.isArray(data.rows)) {
        return wrap(
            <ParamBatchUpdateContent data={data} before={before} />,
            '该工单将修改以下参数行:',
        );
    }

    // 新建 / 批量导入: 本来就没有"变更前", 整体展示提交内容
    if (CREATE_ACTIONS.includes(action) || action.startsWith('import')) {
        return data
            ? wrap(
                  <ContentTable data={data} tone="after" />,
                  '该工单将新增以下内容:',
              )
            : wrap(null, '该工单为新增/导入操作, 内容见原始 JSON');
    }

    // 编辑 / 集合覆盖类: 字段级前后对比
    if (data && before) {
        return wrap(<DiffTable rows={diffRows(data, before, parsed.full)} />);
    }
    return wrap(
        data ? <ContentTable data={data} tone="after" keepEmpty /> : null,
        '提交时未留存变更前快照, 只能展示提交内容',
    );
}

// 决策弹窗 (通过/驳回, 单条/批量共用): 替换浏览器原生 prompt (2026-08-21).
// 驳回必填原因在这里校验; onConfirm 抛错则在弹窗内展示 (批量部分失败的明细).
function DecisionModal({
    open,
    action,
    title,
    onConfirm,
    onClose,
}: {
    open: boolean;
    action: 'approve' | 'reject';
    title: string;
    onConfirm: (note: string) => Promise<void>;
    onClose: () => void;
}) {
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        if (!open) return;
        setNote('');
        setError('');
    }, [open]);
    const isReject = action === 'reject';
    const submit = async () => {
        if (isReject && !note.trim()) {
            setError('驳回必须给出原因');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await onConfirm(note.trim());
            onClose();
        } catch (e) {
            setError(
                e instanceof ApiError
                    ? `${e.code}: ${e.message}`
                    : String(e instanceof Error ? e.message : e),
            );
        } finally {
            setBusy(false);
        }
    };
    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isReject ? '驳回审批' : '通过审批'}
            subtitle={title}
            width={480}
        >
            <div className="space-y-3">
                <div>
                    <label className="field-label">
                        {isReject ? (
                            <>
                                <span className="text-rose-500">* </span>
                                驳回原因 (提交人可见)
                            </>
                        ) : (
                            '决策说明 (可空, 提交人可见)'
                        )}
                    </label>
                    <textarea
                        className="inp w-full h-24 resize-none"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={
                            isReject
                                ? '例: 参数越界, 请核对功率上限后重新提交'
                                : '例: 已与提交人核对'
                        }
                        autoFocus
                    />
                </div>
                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50 whitespace-pre-wrap">
                        {error}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                        disabled={busy}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className={`btn-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                            isReject ? 'bg-rose-600 hover:bg-rose-700' : ''
                        }`}
                        onClick={submit}
                        disabled={busy}
                    >
                        {busy ? '提交中…' : isReject ? '确认驳回' : '确认通过'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

/**
 * 审批工单列表.
 * - 默认 (审批中心 /admin/approvals, admin 菜单): admin 看全部并可审批.
 * - mineOnly (我的工单 /my-approvals, 全员菜单): 只看自己提交的, 纯进度跟踪,
 *   即使当前用户是 admin 也不出审批操作 —— 审批统一在审批中心做.
 */
function ApprovalList({ mineOnly = false }: { mineOnly?: boolean }) {
    // 页面内一切"审批人视角"(全部任务/批量勾选/审批按钮) 都由这个开关统一控制:
    // 对任一资源有 approve 权即进审批中心 (后端列表已按可审批的资源域过滤); 单条按钮再按工单目标资源判.
    const authz = useAuthz();
    const canReview = useCanAny('approve') && !mineOnly;
    const canApproveTask = (t: ApprovalTask) =>
        canReview &&
        allows(authz.rules, approvalResourceOf(t.targetType), 'approve');
    const location = useLocation();
    const history = useHistory();
    // ?kw= 工单号直达 (飞书通知卡片 / 顶栏通知铃铛的链接): 预填搜索词, 且不限状态 ——
    // 默认 status=pending 会把已通过/已驳回的工单滤掉, 点结果通知就看到空表.
    // ?tab= 模块直达 (如人员管理页跳"访问申请").
    // 两者都是"一次性入参": 用完即从地址栏抹掉, 见下面的 useEffect.
    const urlKeyword =
        new URLSearchParams(location.search).get('kw')?.trim() || '';
    const urlTab = new URLSearchParams(location.search).get('tab') || '';
    const [inputKeyword, setInputKeyword] = useState(urlKeyword);

    const [keyword, setKeyword] = useState(urlKeyword);
    const [inputSubmitter, setInputSubmitter] = useState('');
    const [submitter, setSubmitter] = useState('');
    const [status, setStatus] = useState(
        mineOnly || urlKeyword ? '' : 'pending',
    );
    const [scope, setScope] = useState<'all' | 'mine'>(
        mineOnly ? 'mine' : 'all',
    );

    // 模块 tab (按 target_type 过滤) + 各模块 pending 计数徽标
    const [moduleTab, setModuleTab] = useState(() => urlTab || 'all');
    // 直达链接带进来、且用户还没改过的工单号: 它只为"定位那一张", 切模块 tab 时要连同放开的
    // 状态一起复原, 否则从飞书点进来后再点"材料"等 tab, 仍按旧工单号过滤 → 空表 (2026-09-28).
    const linkedKeywordRef = useRef(urlKeyword);

    // ?kw=/?tab= 变化时同步过滤: 从顶栏铃铛点工单进来时本页可能已经挂载 (只换 query 不重挂),
    // 光读初始 state 会出现"地址变了、列表没动". kw 非空时还要放开状态与模块 tab ——
    // 目标工单可能已审批完、也可能不属于当前 tab, 否则直达链接照样是空表.
    // 消费完立刻 replace 掉 query: 否则 kw 一直挂在地址栏上, 用户点"全部"tab / 清空搜索框后
    // 链接仍是 ?kw=AT-xxx, 刷新或分享出去又回到那一条的过滤视图 —— 页内 state 才是唯一事实.
    useEffect(() => {
        if (!urlKeyword && !urlTab) return;
        if (urlKeyword) {
            setInputKeyword(urlKeyword);
            setKeyword(urlKeyword);
            setStatus('');
            setModuleTab('all');
            linkedKeywordRef.current = urlKeyword;
        }
        if (urlTab) setModuleTab(urlTab);
        setPage(1);
        history.replace(location.pathname);
    }, [urlKeyword, urlTab, location.pathname, history]);
    const [stats, setStats] = useState<ApprovalStat[]>([]);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(loadPageSize);
    const [list, setList] = useState<ApprovalTask[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actBusy, setActBusy] = useState<string>(''); // taskCode 维度的禁用
    // 批量审批: 勾选的 taskCode (仅 pending 可选; admin only)
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [batchBusy, setBatchBusy] = useState(false);
    // 通用变更工单: 点标题查看 payload 变更内容
    const [payloadTask, setPayloadTask] = useState<ApprovalTask | null>(null);
    // 工单处理流水 (提交/通过/驳回/评论; 审批执行失败的原因也以 comment 流水呈现, 2026-08-21)
    const [payloadRecords, setPayloadRecords] = useState<
        ApprovalRecord[] | null
    >(null);
    useEffect(() => {
        if (!payloadTask) {
            setPayloadRecords(null);
            return;
        }
        getApproval(payloadTask.taskCode)
            .then((d) => setPayloadRecords(d.records || []))
            .catch(() => setPayloadRecords([]));
    }, [payloadTask]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const activeTab = visibleTabs(mineOnly).find(
                (t) => t.key === moduleTab,
            );
            const res = await listApprovals({
                keyword,
                submitter: submitter || undefined,
                status: status || undefined,
                targetType: activeTab?.targetType,
                page,
                pageSize,
                scope: scope === 'mine' ? 'mine' : undefined,
            });
            setList(res.list || []);
            setTotal(res.totalCount);
            setSelected(new Set());
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
        // 徽标计数与列表同步刷新 (approve/reject 后 load() 会重进这里)
        listApprovalStats(scope === 'mine' ? 'mine' : undefined)
            .then(setStats)
            .catch(() => setStats([]));
    }, [
        keyword,
        submitter,
        status,
        scope,
        page,
        pageSize,
        moduleTab,
        mineOnly,
    ]);

    useEffect(() => {
        load();
    }, [load]);

    const onSwitchTab = (key: string) => {
        if (key === moduleTab) return;
        setModuleTab(key);
        setPage(1);
        const linked = linkedKeywordRef.current;
        if (linked && keyword === linked && inputKeyword.trim() === linked) {
            linkedKeywordRef.current = '';
            setInputKeyword('');
            setKeyword('');
            if (!mineOnly) setStatus('pending');
        }
    };

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const k = inputKeyword.trim();
        const sub = inputSubmitter.trim();
        if (k === keyword && sub === submitter && page === 1) {
            load();
            return;
        }
        setKeyword(k);
        setSubmitter(sub);
        setPage(1);
    };

    // 决策弹窗 (2026-08-21 替换浏览器原生 prompt): 单条 通过/驳回 + 批量 通过/驳回 共用.
    // tasks 为空 = 批量 (取当前勾选); 驳回必填原因由弹窗内校验.
    const [decision, setDecision] = useState<{
        action: 'approve' | 'reject';
        task?: ApprovalTask; // 缺省 = 批量
    } | null>(null);

    const onApprove = (t: ApprovalTask) => {
        if (!canReview) return;
        setDecision({ action: 'approve', task: t });
    };

    const onReject = (t: ApprovalTask) => {
        if (!canReview) return;
        setDecision({ action: 'reject', task: t });
    };

    // 单条决策执行 (弹窗确认后回调)
    const runDecision = async (
        t: ApprovalTask,
        action: 'approve' | 'reject',
        note: string,
    ) => {
        setActBusy(t.taskCode);
        try {
            await (action === 'approve'
                ? approveTask(t.taskCode, note)
                : rejectTask(t.taskCode, note));
            await load();
            emitApprovalChanged(); // 顶栏铃铛徽标立刻跟着变, 不等 60s 轮询
        } finally {
            setActBusy('');
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // --- 批量审批 (admin) ---
    const pendingTasks = list.filter((t) => t.status === 'pending');
    const allChecked =
        pendingTasks.length > 0 &&
        pendingTasks.every((t) => selected.has(t.taskCode));
    const toggleAll = () => {
        if (allChecked) setSelected(new Set());
        else setSelected(new Set(pendingTasks.map((t) => t.taskCode)));
    };
    const toggleOne = (code: string) => {
        const next = new Set(selected);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        setSelected(next);
    };

    // 批量决策入口: 打开同一决策弹窗 (task 缺省), 确认后逐条执行汇总结果
    const batchDecide = (action: 'approve' | 'reject') => {
        if (selected.size === 0) return;
        setDecision({ action });
    };

    // 逐条调 approve/reject (一行一任务), 汇总结果; 失败明细抛给弹窗展示
    const runBatch = async (action: 'approve' | 'reject', note: string) => {
        const codes = Array.from(selected);
        setBatchBusy(true);
        const failed: string[] = [];
        for (const code of codes) {
            try {
                await (action === 'approve'
                    ? approveTask(code, note)
                    : rejectTask(code, note));
            } catch (e) {
                failed.push(
                    `${code}: ${e instanceof ApiError ? e.message : String(e)}`,
                );
            }
        }
        setBatchBusy(false);
        await load();
        emitApprovalChanged();
        if (failed.length > 0) {
            throw new Error(
                `成功 ${codes.length - failed.length} 个, 失败 ${failed.length} 个\n${failed.join('\n')}`,
            );
        }
    };

    return (
        <div className="space-y-5">
            <div>
                <div className="text-base font-semibold">
                    {mineOnly ? '我的工单' : '审批中心'}
                </div>
                <div className="text-xs text-slate-500">
                    {mineOnly
                        ? '参数行审批 + 主数据变更工单 (材料/SKU/类别/设备/效果图/工具/小贴士/工艺类型) · '
                        : '参数行审批 + 主数据变更工单 (材料/SKU/字典/类别/设备/效果图/工具/平台配置) · '}
                    {mineOnly
                        ? '我提交的全部工单及其审批进度; 审批由管理员处理'
                        : canReview
                          ? '管理员: 默认看全部, 可切到仅自己提交'
                          : '普通用户: 只能看自己提交的任务'}
                </div>
            </div>

            {/* 模块 tab: 按 target_type 过滤 + pending 计数徽标 */}
            <div className="flex flex-wrap gap-2">
                {visibleTabs(mineOnly).map((t) => {
                    const n = tabPendingCount(t, stats);
                    const active = t.key === moduleTab;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => onSwitchTab(t.key)}
                            className={`px-3 py-1.5 text-sm rounded inline-flex items-center gap-1.5 ${
                                active
                                    ? 'bg-brand-500 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            {t.label}
                            {n > 0 && (
                                <span
                                    className={`text-[11px] leading-none px-1.5 py-0.5 rounded-full ${
                                        active
                                            ? 'bg-white/25 text-white'
                                            : 'bg-amber-100 text-amber-700'
                                    }`}
                                    title={`${n} 个待审批`}
                                >
                                    {n}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <form
                onSubmit={onSearch}
                className="card p-3 flex flex-wrap gap-2 items-end"
            >
                <input
                    className="inp w-64"
                    placeholder="标题 / 参数集 id"
                    value={inputKeyword}
                    onChange={(e) => setInputKeyword(e.target.value)}
                    onBlur={() => {
                        // 清空后失焦直接恢复默认列表, 不必再点"查询"
                        if (inputKeyword.trim() === '' && keyword !== '') {
                            setKeyword('');
                            setPage(1);
                        }
                    }}
                />
                {!mineOnly && (
                    <input
                        className="inp w-48"
                        placeholder="提交人 (名字 / 邮箱)"
                        value={inputSubmitter}
                        onChange={(e) => setInputSubmitter(e.target.value)}
                        onBlur={() => {
                            if (
                                inputSubmitter.trim() === '' &&
                                submitter !== ''
                            ) {
                                setSubmitter('');
                                setPage(1);
                            }
                        }}
                    />
                )}
                {/* 下拉筛选切换即查 (关键词才需要回车/点查询) */}
                <SearchSelect
                    className="w-32"
                    value={status}
                    onChange={(v) => {
                        setStatus(v);
                        setPage(1);
                    }}
                    placeholder="全部状态"
                    options={APPROVAL_STATUS_OPTS}
                    clearable
                />
                {canReview && (
                    <SearchSelect
                        className="w-32"
                        value={scope}
                        onChange={(v) => {
                            setScope(v as 'all' | 'mine');
                            setPage(1);
                        }}
                        options={SCOPE_OPTS}
                    />
                )}
                <button type="submit" className="btn-ghost">
                    查询
                </button>
                <span className="ml-auto text-xs text-slate-400">
                    共 {total} 条
                </span>
            </form>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {/* 批量审批条 (admin, 有勾选时浮出) */}
            {canReview && selected.size > 0 && (
                <div className="card p-3 flex items-center gap-3 border-brand-200 bg-brand-50/50">
                    <span className="text-sm">
                        已勾选 <b>{selected.size}</b> 个待审批任务
                    </span>
                    <button
                        type="button"
                        className="btn-primary text-xs"
                        onClick={() => batchDecide('approve')}
                        disabled={batchBusy}
                    >
                        {batchBusy ? '处理中…' : `批量通过 (${selected.size})`}
                    </button>
                    <button
                        type="button"
                        className="btn-ghost text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => batchDecide('reject')}
                        disabled={batchBusy}
                    >
                        批量驳回
                    </button>
                    <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => setSelected(new Set())}
                        disabled={batchBusy}
                    >
                        取消勾选
                    </button>
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            {canReview && (
                                <th
                                    className="th w-8"
                                    title="勾选待审批任务, 批量通过/驳回"
                                >
                                    <input
                                        type="checkbox"
                                        checked={allChecked}
                                        disabled={pendingTasks.length === 0}
                                        onChange={toggleAll}
                                    />
                                </th>
                            )}
                            <th className="th">任务编号</th>
                            <th className="th">标题</th>
                            <th className="th">变更对象</th>
                            <th className="th">提交人</th>
                            <th className="th">状态</th>
                            <th className="th">提交时间</th>
                            <th className="th">决策时间</th>
                            <th className="th">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={canReview ? 9 : 8}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={canReview ? 9 : 8}
                                >
                                    暂无任务
                                </td>
                            </tr>
                        )}
                        {list.map((t) => {
                            const s = STATUS_LABEL[t.status] || {
                                label: t.status,
                                cls: 'bg-slate-100 text-slate-500',
                            };
                            const canAct =
                                canApproveTask(t) && t.status === 'pending';
                            // targetType 在资源表内 = 通用变更工单; 否则为参数行审批 (targetType = 工艺框架 code)
                            const resourceLabel = RESOURCE_LABEL[t.targetType];
                            return (
                                <tr key={t.id} className="hover:bg-slate-50">
                                    {canReview && (
                                        <td className="td">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(
                                                    t.taskCode,
                                                )}
                                                disabled={
                                                    t.status !== 'pending'
                                                }
                                                title={
                                                    t.status === 'pending'
                                                        ? '勾选后可批量通过/驳回'
                                                        : '仅待审批任务可勾选'
                                                }
                                                onChange={() =>
                                                    toggleOne(t.taskCode)
                                                }
                                            />
                                        </td>
                                    )}
                                    <td className="td font-mono text-xs">
                                        {t.taskCode}
                                    </td>
                                    <td className="td">
                                        {hasPayload(t) ? (
                                            <button
                                                type="button"
                                                className="text-left text-brand-700 hover:underline"
                                                title="点击查看变更内容 (payload)"
                                                onClick={() =>
                                                    setPayloadTask(t)
                                                }
                                            >
                                                {t.title}
                                            </button>
                                        ) : (
                                            <div>{t.title}</div>
                                        )}
                                        {t.changeSummary && (
                                            <div className="text-[11px] text-slate-400 truncate max-w-[260px]">
                                                {t.changeSummary}
                                            </div>
                                        )}
                                    </td>
                                    <td className="td font-mono text-xs">
                                        {resourceLabel ? (
                                            // 通用变更工单: 资源中文名 + targetCode; material 跳材料详情
                                            t.targetType === 'material' ? (
                                                <PermissionLink
                                                    resource="material"
                                                    hide
                                                    to={`/materials/${encodeURIComponent(t.targetCode)}`}
                                                    className="text-brand-600 hover:underline"
                                                >
                                                    {resourceLabel} ·{' '}
                                                    {t.targetCode}
                                                </PermissionLink>
                                            ) : (
                                                <span>
                                                    {resourceLabel} ·{' '}
                                                    {t.targetCode}
                                                </span>
                                            )
                                        ) : (
                                            /* 参数行审批: targetType = arch_code, targetCode = 参数行 id */
                                            <PermissionLink
                                                resource="param_row"
                                                hide
                                                to={`/param-sets/manage?arch=${encodeURIComponent(t.targetType)}&id=${encodeURIComponent(t.targetCode)}`}
                                                className="text-brand-600 hover:underline"
                                            >
                                                {t.targetType}#{t.targetCode}
                                            </PermissionLink>
                                        )}
                                    </td>
                                    <td className="td text-xs">
                                        {t.submittedByName || t.submittedBy}
                                    </td>
                                    <td className="td">
                                        <span className={`tag ${s.cls}`}>
                                            {s.label}
                                        </span>
                                    </td>
                                    <td className="td text-[11px] text-slate-500">
                                        {formatTime(t.createdAt)}
                                    </td>
                                    <td className="td text-[11px] text-slate-500">
                                        {formatTime(t.decidedAt)}
                                    </td>
                                    <td className="td">
                                        {canAct ? (
                                            <div className="flex gap-3 text-xs whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    className="text-brand-600 hover:underline disabled:opacity-50"
                                                    onClick={() => onApprove(t)}
                                                    disabled={
                                                        actBusy === t.taskCode
                                                    }
                                                >
                                                    通过
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-rose-500 hover:underline disabled:opacity-50"
                                                    onClick={() => onReject(t)}
                                                    disabled={
                                                        actBusy === t.taskCode
                                                    }
                                                >
                                                    驳回
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-300">
                                                —
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <div className="flex items-center gap-2">
                    <SearchSelect
                        className="w-28"
                        value={String(pageSize)}
                        options={PAGE_SIZE_OPTIONS.map((n) => ({
                            value: String(n),
                            label: `${n} 条/页`,
                        }))}
                        onChange={(v) => {
                            const n = Number(v);
                            localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(n));
                            setPageSize(n);
                            setPage(1);
                        }}
                    />
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                </div>
            </div>

            {/* 变更内容弹窗 (参数行审批 + 变更工单同一套): 有 before 快照走字段级前后对比,
                新建/导入/删除类按动作语义整体展示 */}
            {payloadTask && (
                <Modal
                    open
                    width={640}
                    title={`变更内容 · ${payloadTask.taskCode}`}
                    onClose={() => setPayloadTask(null)}
                >
                    <div className="space-y-3">
                        {/* 操作摘要: 谁 / 做了什么 / 对哪个对象 / 什么时候 —— 审批人先看清意图再看字段 */}
                        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                            <span className="text-slate-400">操作</span>
                            <span className="font-medium">
                                {actionLabelOf(payloadTask)}
                                <span className="text-slate-400 font-normal">
                                    {' · '}
                                    {RESOURCE_LABEL[payloadTask.targetType] ||
                                        payloadTask.targetType}
                                </span>
                            </span>
                            <span className="text-slate-400">对象</span>
                            <span className="font-mono break-all">
                                {payloadTask.targetCode}
                            </span>
                            <span className="text-slate-400">提交</span>
                            <span>
                                {payloadTask.submittedByName ||
                                    payloadTask.submittedBy ||
                                    '—'}
                                {payloadTask.createdAt && (
                                    <span className="text-slate-400">
                                        {' · '}
                                        {payloadTask.createdAt}
                                    </span>
                                )}
                            </span>
                        </div>
                        <PayloadContent payload={payloadTask.payload || ''} />

                        {/* 处理流水: 提交/通过/驳回/评论; "审批执行失败"的原因也在这里 (comment 流水) */}
                        {payloadRecords && payloadRecords.length > 0 && (
                            <div>
                                <div className="text-xs font-semibold text-slate-500 mb-1.5">
                                    处理流水
                                </div>
                                <div className="space-y-1 max-h-44 overflow-auto">
                                    {payloadRecords.map((r) => {
                                        const isFail =
                                            r.action === 'comment' &&
                                            (r.comment || '').includes('失败');
                                        const actionLabel =
                                            {
                                                submit: '提交',
                                                approve: '通过',
                                                reject: '驳回',
                                                withdraw: '撤回',
                                                comment: '评论',
                                            }[r.action] || r.action;
                                        return (
                                            <div
                                                key={r.id}
                                                className={`text-xs rounded px-2.5 py-1.5 border ${
                                                    isFail
                                                        ? 'border-rose-200 bg-rose-50 text-rose-700'
                                                        : 'border-slate-100 bg-slate-50 text-slate-600'
                                                }`}
                                            >
                                                <span className="font-medium">
                                                    {isFail
                                                        ? '⚠ 执行失败'
                                                        : actionLabel}
                                                </span>
                                                <span className="text-slate-400">
                                                    {' '}
                                                    · {formatTime(r.createdAt)}
                                                </span>
                                                {r.comment && (
                                                    <div className="mt-0.5 break-all">
                                                        {r.comment}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => setPayloadTask(null)}
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {decision && (
                <DecisionModal
                    open
                    action={decision.action}
                    title={
                        decision.task
                            ? decision.task.title
                            : `批量${decision.action === 'approve' ? '通过' : '驳回'} ${selected.size} 个任务 (说明各任务共用)`
                    }
                    onConfirm={async (note) => {
                        if (decision.task) {
                            await runDecision(
                                decision.task,
                                decision.action,
                                note,
                            );
                        } else {
                            await runBatch(decision.action, note);
                        }
                    }}
                    onClose={() => setDecision(null)}
                />
            )}
        </div>
    );
}

export default ApprovalList;
